import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FiCheck,
  FiStar,
  FiTrendingUp,
  FiShield,
  FiZap,
  FiClock,
  FiDollarSign,
  FiArrowRight,
} from 'react-icons/fi';

interface OfferData {
  id: string;
  name: string;
  bonus: string;
  rating: number;
  image: string;
  url: string;
  description: string;
  pros: string[];
  cons: string[];
  minDeposit: string;
  withdrawalTime: string;
  license: string;
  features: string[];
  chartData: { month: string; value: number }[];
}

const OfferReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedTab, setSelectedTab] = useState<'overview' | 'bonus' | 'reviews'>('overview');

  const offers: Record<string, OfferData> = {
    circusbet: {
      id: 'circusbet',
      name: 'CircusBet',
      bonus: '100€ + 50 tours gratuits',
      rating: 4.8,
      image: '/images/offers/circusbet.webp',
      url: 'https://www.circusbet.fr/',
      description:
        'CircusBet se distingue par son bonus de bienvenue généreux et son interface intuitive. Avec des cotes compétitives sur tous les sports majeurs, c\'est un choix idéal pour les parieurs français.',
      pros: [
        'Bonus de bienvenue très attractif',
        'Interface utilisateur moderne et intuitive',
        'Support client disponible 24/7',
        'Application mobile performante',
        'Cotes compétitives sur le football',
      ],
      cons: [
        'Limite de retrait quotidienne',
        'Peu de promotions régulières',
      ],
      minDeposit: '10€',
      withdrawalTime: '24-48h',
      license: 'ANJ',
      features: [
        'Pari en direct',
        'Cash-out',
        'Streaming live',
        'Pari combiné',
        'Boost de cotes',
      ],
      chartData: [
        { month: 'Jan', value: 85 },
        { month: 'Fév', value: 88 },
        { month: 'Mar', value: 90 },
        { month: 'Avr', value: 87 },
        { month: 'Mai', value: 92 },
        { month: 'Juin', value: 94 },
      ],
    },
    genybet: {
      id: 'genybet',
      name: 'GenyBet',
      bonus: '150€ de bonus',
      rating: 4.7,
      image: '/images/offers/genybet.webp',
      url: 'https://www.genybet.fr/',
      description:
        'GenyBet offre une expérience de paris premium avec des fonctionnalités avancées et un service client de qualité. Leur bonus de bienvenue est l\'un des plus généreux du marché français.',
      pros: [
        'Bonus de bienvenue exceptionnel',
        'Paiements ultra-rapides',
        'Programme VIP exclusif',
        'Large sélection de sports',
        'Cotes boostées régulièrement',
      ],
      cons: [
        'Interface peut être complexe pour débutants',
        'Conditions de mise pour le bonus',
      ],
      minDeposit: '20€',
      withdrawalTime: '12-24h',
      license: 'ANJ',
      features: [
        'Pari en direct',
        'Cash-out',
        'Streaming live',
        'Pari combiné',
        'Boost de cotes',
        'Programme VIP',
      ],
      chartData: [
        { month: 'Jan', value: 82 },
        { month: 'Fév', value: 85 },
        { month: 'Mar', value: 88 },
        { month: 'Avr', value: 90 },
        { month: 'Mai', value: 91 },
        { month: 'Juin', value: 93 },
      ],
    },
    netbet: {
      id: 'netbet',
      name: 'NetBet',
      bonus: '200€ + 100 tours',
      rating: 4.9,
      image: '/images/offers/netbet.webp',
      url: 'https://www.netbet.fr/',
      description:
        'NetBet est le leader du marché français avec les meilleures cotes et un service client irréprochable. Leur programme de fidélité et leurs promotions régulières en font un choix premium.',
      pros: [
        'Meilleures cotes du marché',
        'Cashback hebdomadaire',
        'Programme de fidélité avantageux',
        'Support multilingue',
        'Retraits instantanés',
      ],
      cons: [
        'Bonus nécessite un dépôt plus élevé',
        'Interface chargée',
      ],
      minDeposit: '15€',
      withdrawalTime: 'Instant',
      license: 'ANJ',
      features: [
        'Pari en direct',
        'Cash-out',
        'Streaming live',
        'Pari combiné',
        'Boost de cotes',
        'Cashback',
        'Programme VIP',
      ],
      chartData: [
        { month: 'Jan', value: 88 },
        { month: 'Fév', value: 90 },
        { month: 'Mar', value: 92 },
        { month: 'Avr', value: 94 },
        { month: 'Mai', value: 95 },
        { month: 'Juin', value: 96 },
      ],
    },
  };

  const offer = offers[id || ''];

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Offre non trouvée</h1>
          <Link to="/" className="text-accent-primary hover:text-accent-secondary">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  const maxValue = Math.max(...offer.chartData.map((d) => d.value));

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Review',
            itemReviewed: {
              '@type': 'Organization',
              name: offer.name,
            },
            reviewRating: {
              '@type': 'Rating',
              ratingValue: offer.rating,
              bestRating: 5,
            },
            author: {
              '@type': 'Organization',
              name: "Let's Try Bet",
            },
            reviewBody: offer.description,
          }),
        }}
      />

      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-dark-surface to-dark-card py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <FiStar
                      key={i}
                      className={
                        i < Math.floor(offer.rating)
                          ? 'text-yellow-400 fill-current'
                          : 'text-dark-muted'
                      }
                      size={24}
                    />
                  ))}
                  <span className="ml-3 text-white text-xl font-semibold">
                    {offer.rating}/5
                  </span>
                </div>
                <h1 className="text-5xl font-bold text-white mb-6">{offer.name}</h1>
                <p className="text-xl text-dark-muted mb-8">{offer.description}</p>
                <div className="bg-accent-primary text-white font-bold text-2xl py-4 px-8 rounded-lg inline-block mb-8">
                  {offer.bonus}
                </div>
                <a
                  href={offer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-4 px-8 rounded-lg inline-flex items-center transition-colors"
                >
                  Réclamer le bonus <FiArrowRight className="ml-2" />
                </a>
              </div>
              <div className="bg-dark-card border border-dark-border rounded-lg p-8">
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-full h-auto"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <FiDollarSign className="mx-auto mb-2 text-accent-primary" size={32} />
                <div className="text-2xl font-bold text-white">{offer.minDeposit}</div>
                <div className="text-dark-muted text-sm">Dépôt minimum</div>
              </div>
              <div className="text-center">
                <FiClock className="mx-auto mb-2 text-accent-primary" size={32} />
                <div className="text-2xl font-bold text-white">{offer.withdrawalTime}</div>
                <div className="text-dark-muted text-sm">Retrait</div>
              </div>
              <div className="text-center">
                <FiShield className="mx-auto mb-2 text-accent-primary" size={32} />
                <div className="text-2xl font-bold text-white">{offer.license}</div>
                <div className="text-dark-muted text-sm">Licence</div>
              </div>
              <div className="text-center">
                <FiTrendingUp className="mx-auto mb-2 text-accent-primary" size={32} />
                <div className="text-2xl font-bold text-white">{offer.rating}/5</div>
                <div className="text-dark-muted text-sm">Note</div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="flex space-x-4 mb-8 border-b border-dark-border">
              {[
                { id: 'overview', label: 'Aperçu' },
                { id: 'bonus', label: 'Bonus' },
                { id: 'reviews', label: 'Avis' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id as any)}
                  className={`pb-4 px-6 font-semibold transition-colors ${
                    selectedTab === tab.id
                      ? 'text-accent-primary border-b-2 border-accent-primary'
                      : 'text-dark-muted hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {selectedTab === 'overview' && (
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Avantages</h3>
                  <ul className="space-y-3">
                    {offer.pros.map((pro, i) => (
                      <li key={i} className="flex items-start">
                        <FiCheck className="text-accent-success mr-3 mt-1 flex-shrink-0" />
                        <span className="text-dark-muted">{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Points à améliorer</h3>
                  <ul className="space-y-3">
                    {offer.cons.map((con, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-red-400 mr-3">×</span>
                        <span className="text-dark-muted">{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Bonus Tab */}
            {selectedTab === 'bonus' && (
              <div className="bg-dark-card border border-dark-border rounded-lg p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Détails du Bonus</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Bonus de bienvenue</h4>
                    <p className="text-dark-muted">{offer.bonus}</p>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Conditions</h4>
                    <ul className="list-disc list-inside text-dark-muted space-y-2">
                      <li>Dépôt minimum requis: {offer.minDeposit}</li>
                      <li>Mise requise: 3x le montant du bonus</li>
                      <li>Validité: 30 jours</li>
                      <li>Bonus non retirable avant validation des conditions</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Reviews Tab */}
            {selectedTab === 'reviews' && (
              <div className="space-y-6">
                {[
                  {
                    author: 'Jean D.',
                    rating: 5,
                    comment:
                      'Excellent bookmaker, retraits rapides et interface intuitive. Je recommande !',
                  },
                  {
                    author: 'Marie L.',
                    rating: 4,
                    comment:
                      'Très bon service, quelques améliorations possibles sur les cotes mais globalement satisfait.',
                  },
                ].map((review, i) => (
                  <div
                    key={i}
                    className="bg-dark-card border border-dark-border rounded-lg p-6"
                  >
                    <div className="flex items-center mb-3">
                      <div className="flex">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <FiStar
                            key={j}
                            className={
                              j < review.rating
                                ? 'text-yellow-400 fill-current'
                                : 'text-dark-muted'
                            }
                            size={16}
                          />
                        ))}
                      </div>
                      <span className="ml-3 text-white font-semibold">{review.author}</span>
                    </div>
                    <p className="text-dark-muted">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Chart Section */}
        <section className="py-12 bg-dark-surface">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Évolution de la Satisfaction
            </h2>
            <div className="bg-dark-card border border-dark-border rounded-lg p-8">
              <div className="flex items-end justify-between h-80 space-x-3 md:space-x-4 pb-8">
                {offer.chartData.map((data, i) => {
                  const barHeight = (data.value / maxValue) * 100;
                  const minHeight = 10; // Минимальная высота для видимости
                  const finalHeight = Math.max(barHeight, minHeight);
                  
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center h-full">
                      <div className="w-full flex items-end justify-center h-full relative">
                        <div
                          className="w-full bg-gradient-to-t from-accent-primary to-accent-secondary rounded-t-lg transition-all hover:opacity-80 hover:scale-105 cursor-pointer relative group"
                          style={{
                            height: `${finalHeight}%`,
                            minHeight: `${minHeight}px`,
                          }}
                          title={`${data.value}% - ${data.month}`}
                        >
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-dark-surface border border-dark-border rounded px-2 py-1 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            {data.value}%
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 text-dark-muted text-sm font-medium">{data.month}</div>
                      <div className="text-white font-semibold mt-1 text-xs">{data.value}%</div>
                    </div>
                  );
                })}
              </div>
              {/* Chart Legend */}
              <div className="mt-6 pt-6 border-t border-dark-border flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gradient-to-t from-accent-primary to-accent-secondary rounded"></div>
                  <span className="text-dark-muted">Satisfaction (%)</span>
                </div>
                <div className="text-dark-muted">|</div>
                <div className="text-dark-muted">
                  Période: {offer.chartData[0].month} - {offer.chartData[offer.chartData.length - 1].month}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Fonctionnalités</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {offer.features.map((feature, i) => (
                <div
                  key={i}
                  className="bg-dark-card border border-dark-border rounded-lg p-6 text-center hover:border-accent-primary transition-all"
                >
                  <FiZap className="mx-auto mb-3 text-accent-primary" size={32} />
                  <div className="text-white font-semibold">{feature}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-accent-primary to-accent-secondary">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Prêt à commencer avec {offer.name} ?
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Réclamez votre bonus de bienvenue maintenant et commencez à parier dès aujourd'hui.
            </p>
            <a
              href={offer.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-accent-primary font-semibold py-4 px-8 rounded-lg hover:bg-white/90 transition-colors"
            >
              S'inscrire maintenant
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default OfferReview;

